import { constVoid, DOM, flow, IO, IOO, O, pipe, RNEA, RA, RR, s, v } from '@packages/prelude'
import { Swiper, Navigation, Pagination, Thumbs, EffectFade } from 'swiper'
import { type SwiperOptions } from 'swiper/types'
import 'bootstrap'
import '../css/main.scss'
import { constFalse } from 'fp-ts/function'

pipe(
  IOO.of({ document }),
  IOO.bind('headerDropdown', ({ document }) => pipe(document, DOM.querySelector('.header-dropdown > .dropdown-toggle'))),
  IOO.bind('headerBackdrop', ({ document }) => pipe(document, DOM.querySelector('.header-backdrop'))),
  IOO.bind('navBarTransparent', ({ document }) => pipe(document, DOM.querySelector('.navbar-transparent'))),
  IOO.map(({ headerDropdown, headerBackdrop, navBarTransparent }) => {
    DOM.addEventListener('scroll')(() => () => {
      navBarTransparent.classList.toggle('bg-primary', window.scrollY > 0)
    })(document)()
    DOM.addEventListener('click')(() => () => {
      if (!window.scrollY) navBarTransparent.classList.toggle('bg-primary')
    })(headerDropdown)()
    DOM.addEventListener('click')(() => () => {
      if (!window.scrollY) navBarTransparent.classList.remove('bg-primary')
    })(headerBackdrop)()
  })
)()

pipe(
  IOO.of({ document }),
  IOO.bind('dropDownMaxHeight', ({ document }) =>
    pipe(
      document,
      DOM.querySelector('.navbar'),
      IOO.map(navbar => `calc(100vh - ${navbar.clientHeight}px - 6rem)`)
    )
  ),
  IOO.bind('productDropdown', ({ document }) => pipe(document, DOM.querySelector('.dropdown-row')) as IOO.IOOption<HTMLElement>),
  IOO.chainFirstIOK(
    ({ productDropdown, dropDownMaxHeight }) =>
      () =>
        (productDropdown.style.maxHeight = dropDownMaxHeight)
  ),
  IOO.bind('categoryDropdowns', ({ productDropdown }) => pipe(productDropdown, DOM.querySelectorAll('.header-sub-dropdown'))),
  IOO.chain(({ dropDownMaxHeight, productDropdown, categoryDropdowns }) =>
    pipe(
      categoryDropdowns,
      RA.map(dropdown =>
        pipe(
          dropdown,
          DOM.querySelector('.dropdown-menu') as IO.IO<IOO.IOOption<HTMLElement>>,
          IOO.chainFirstIOK(categorySubDropdown => () => (categorySubDropdown.style.maxHeight = dropDownMaxHeight)),
          IOO.chainIOK(categorySubDropdown =>
            pipe(
              dropdown,
              DOM.addEventListener('click')(() => () => {
                productDropdown.style.height =
                  categorySubDropdown.scrollHeight > productDropdown.clientHeight
                    ? `${categorySubDropdown.scrollHeight}px`
                    : 'auto'
              })
            )
          )
        )
      ),
      RA.sequence(IOO.Applicative)
    )
  )
)()

const makeSwiper = (selector: string, options: SwiperOptions) =>
  pipe(
    IOO.of({ document }),
    IOO.chain(({ document }) => pipe(document, DOM.querySelector(`#${selector}`)) as IOO.IOOption<HTMLElement>),
    IOO.fold(
      () => constVoid,
      element => IOO.of(new Swiper(element, options))
    )
  )()

const canLoop =
  (selector: string, min = 3) =>
  (document: Document) => {
    return pipe(
      IOO.of({ document }),
      IOO.bind('slides', ({ document }) => pipe(document, DOM.querySelectorAll(`#${selector} .swiper-slide`))),
      IOO.match(constFalse, ({ slides }) => slides.length > min)
    )
  }

const thumbsSwiper = pipe(
  IOO.of({ document }),
  IOO.chain(({ document }) =>
    pipe(
      document,
      DOM.querySelector('#productThumbsCarousel'),
      IOO.map(
        element =>
          new Swiper(element as HTMLElement, {
            loop: pipe(document, canLoop('productThumbsCarousel', 1))(),
            speed: 750,
            slidesPerView: 'auto'
          })
      )
    )
  )
)()

const getSwipersOptions = (document: Document): Record<string, SwiperOptions> => ({
  categoryCarousel: {
    slidesPerView: 2,
    spaceBetween: 20,
    loop: true,
    speed: 750,
    modules: [Navigation],
    breakpoints: {
      992: {
        slidesPerView: 3,
        spaceBetween: 30
      }
    },
    navigation: {
      nextEl: '#categoryNext',
      prevEl: '#categoryPrev'
    }
  },
  reviewCarousel: {
    loop: true,
    speed: 750,
    spaceBetween: 30,
    modules: [Navigation, Pagination],
    pagination: {
      el: '.swiper-pagination',
      clickable: true
    },
    navigation: {
      nextEl: '#reviewNext',
      prevEl: '#reviewPrev'
    }
  },
  socialsCarousel: {
    loop: true,
    speed: 750,
    slidesPerView: 2,
    spaceBetween: 20,
    modules: [Navigation],
    navigation: {
      nextEl: '#socialsNext',
      prevEl: '#socialsPrev'
    },
    breakpoints: {
      576: {
        slidesPerView: 4,
        spaceBetween: 30,
        allowTouchMove: false
      }
    }
  },
  productPreviewCarousel: {
    slidesPerView: 2,
    spaceBetween: 20,
    freeMode: true,
    loop: pipe(document, canLoop('productPreviewCarousel'))(),
    speed: 750,
    modules: [Navigation],
    navigation: {
      nextEl: '#productPreviewNext',
      prevEl: '#productPreviewPrev'
    },
    breakpoints: {
      768: {
        spaceBetween: 30
      }
    }
  },
  productGalleryCarousel: {
    loop: pipe(document, canLoop('productGalleryCarousel'))(),
    speed: 750,
    spaceBetween: 24,
    modules: [Navigation, Pagination, Thumbs],
    pagination: {
      el: '.swiper-pagination'
    },
    navigation: {
      nextEl: '#productGalleryNext',
      prevEl: '#productGalleryPrev'
    },
    thumbs: {
      swiper: pipe(
        thumbsSwiper,
        O.fold(
          () => null,
          thumb => {
            return thumb
          }
        )
      )
    },
    on: {
      realIndexChange: ({ realIndex }) => {
        pipe(
          thumbsSwiper,
          O.fold(
            () => null,
            thumb => thumb.slideToLoop(realIndex)
          )
        )
      }
    }
  }
})

pipe(
  document,
  getSwipersOptions,
  RR.foldMapWithIndex(s.Ord)(v.Monoid)((selector, options) => makeSwiper(selector, options))
)

const makePreviewSwiper = (selector: string, options: SwiperOptions) =>
  pipe(
    IOO.of({ document }),
    IOO.chain(
      ({ document }) =>
        pipe(document, DOM.querySelectorAll(`.${selector}`)) as unknown as IOO.IOOption<RNEA.ReadonlyNonEmptyArray<HTMLElement>>
    ),
    IOO.map(element =>
      pipe(
        element,
        RNEA.map(el => IOO.of(new Swiper(el, options)))
      )
    )
  )()

const PreviewSwipers: Record<string, SwiperOptions> = {
  productPreviewImageCarousel: {
    loop: true,
    modules: [EffectFade, Navigation, Pagination],
    pagination: {
      el: '.swiper-pagination'
    },
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev'
    },
    effect: 'fade',
    autoplay: false,
    speed: 750
  }
}

pipe(
  PreviewSwipers,
  RR.foldMapWithIndex(s.Ord)(v.Monoid)((selector, options) => makePreviewSwiper(selector, options))
)

// Reset filter on post archive pages
window.addEventListener('DOMContentLoaded', () => {
  const resetForm = pipe(
    IOO.of({ document }),
    IOO.bind('form', ({ document }) => pipe(document, DOM.querySelector('form#posts-filter')) as IOO.IOOption<HTMLFormElement>),
    IOO.bind(
      'inputs',
      ({ form }) =>
        pipe(form, DOM.querySelectorAll('input[type="checkbox"]')) as unknown as IOO.IOOption<
          RNEA.ReadonlyNonEmptyArray<HTMLInputElement>
        >
    ),
    IOO.chainIOK(({ form, inputs }) =>
      pipe(
        inputs,
        RNEA.traverse(IO.Applicative)(input => IO.of((input.checked = false))),
        IO.chain(IO.of(() => form.submit()))
      )
    )
  )

  pipe(
    IOO.of({ document }),
    IOO.chain(({ document }) => pipe(document, DOM.querySelector('#reset-filter')) as IOO.IOOption<HTMLButtonElement>),
    IOO.chainIOK(flow(DOM.addEventListener('click')(() => resetForm)))
  )()
})
